import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Link, useNavigate, type LinkProps } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { uiBookmarks } from '../api/ui-bookmarks';

/**
 * Per-user settings that change how the app behaves, saved with the user's
 * other UI state (the ui-bookmarks store, namespace USER_PREFERENCES), so they
 * follow the person to any browser.
 */
export interface UserPreferences {
  /**
   * Records (an order, a ticket, a PO, a shipment, a company) open in a new
   * tab. On by default: that is how the app behaved before this was a setting,
   * and it keeps a filtered list in place behind the record.
   */
  openInNewTab: boolean;
  /**
   * The customer / shipping / financials block on an order's page is folded to
   * one summary line. Folded by default: the items are what most visits are
   * for, and the summary line still shows who, where and how much.
   */
  orderInfoCollapsed: boolean;
}

export const DEFAULT_PREFERENCES: UserPreferences = { openInNewTab: true, orderInfoCollapsed: true };

/** Must match the API's bookmark pattern: lowercase, [a-z0-9_.-]. */
const USER_PREFERENCES = 'user_preferences';

interface Ctx {
  preferences: UserPreferences;
  setPreference: <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => Promise<void>;
}

const PreferencesContext = createContext<Ctx>({
  preferences: DEFAULT_PREFERENCES,
  setPreference: async () => {},
});

export const UserPreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  // The signed-in user, as the login response identifies them. It carries no
  // id (the API knows the user from the token), so the email is the key: a
  // check on `user.id` here never passed, and the saved preference was never
  // read back - the default won on every page load.
  const userKey = user?.email ?? null;
  const [preferences, setPreferences] = useState<UserPreferences>(DEFAULT_PREFERENCES);

  // Load per user; defaults until it answers, or if it cannot.
  useEffect(() => {
    let alive = true;
    setPreferences(DEFAULT_PREFERENCES);
    if (!userKey) return;
    uiBookmarks
      .get<Partial<UserPreferences>>(USER_PREFERENCES)
      .then((res) => alive && setPreferences({ ...DEFAULT_PREFERENCES, ...(res.config || {}) }))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [userKey]);

  const setPreference = useCallback<Ctx['setPreference']>(
    async (key, value) => {
      const next = { ...preferences, [key]: value };
      setPreferences(next);
      await uiBookmarks.save(USER_PREFERENCES, next);
    },
    [preferences],
  );

  return <PreferencesContext.Provider value={{ preferences, setPreference }}>{children}</PreferencesContext.Provider>;
};

export const useUserPreferences = () => useContext(PreferencesContext);

/** Cmd / Ctrl / Shift / middle click always means "somewhere else", whatever the setting. */
const wantsNewTab = (e?: { metaKey?: boolean; ctrlKey?: boolean; shiftKey?: boolean; button?: number }) =>
  !!e && (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1);

/**
 * Opens an in-app record the way the user prefers: a new tab, or this one.
 * For links to other sites and files, keep target="_blank" - this is only for
 * the app's own pages.
 */
export const useOpenRecord = () => {
  const navigate = useNavigate();
  const { preferences } = useUserPreferences();
  return useCallback(
    (path: string, e?: { metaKey?: boolean; ctrlKey?: boolean; shiftKey?: boolean; button?: number }) => {
      if (preferences.openInNewTab || wantsNewTab(e)) window.open(path, '_blank', 'noopener');
      else navigate(path);
    },
    [navigate, preferences.openInNewTab],
  );
};

/** A <Link> to an in-app record that follows the new-tab setting. */
export const RecordLink: React.FC<LinkProps> = (props) => {
  const { preferences } = useUserPreferences();
  return preferences.openInNewTab ? (
    <Link {...props} target="_blank" rel="noopener noreferrer" />
  ) : (
    <Link {...props} />
  );
};
